import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '../services/api'

interface User {
  id: number
  username: string
  email: string
  full_name: string
  role: string
  distributor_id?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token')
      if (savedToken) {
        try {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`
          // Verify token is still valid by making a test request
          const response = await apiClient.get('/api/v1/protected')
          if (response.data.user) {
            setToken(savedToken)
            // You'd typically decode the JWT to get user info
            // For now, we'll make another request to get user details
            // setUser(response.data.user)
          }
        } catch (error) {
          localStorage.removeItem('token')
          delete apiClient.defaults.headers.common['Authorization']
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (username: string, password: string) => {
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)

    const response = await apiClient.post('/api/v1/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })

    const { access_token } = response.data
    setToken(access_token)
    localStorage.setItem('token', access_token)
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

    // Decode JWT or fetch user info
    // For now, we'll create a basic user object
    setUser({
      id: 1,
      username,
      email: `${username}@example.com`,
      full_name: username,
      role: 'distributor'
    })
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem('token')
    delete apiClient.defaults.headers.common['Authorization']
  }

  const value = {
    user,
    token,
    login,
    logout,
    isLoading
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
